/**
 * Multi-select variant selector.
 *
 * Family groups (Dad, Mom, Boy, Girl, ...) render as independent groups.
 * Customers can select multiple sizes per group, set a quantity for each
 * resolved variant, and add everything to the bag in one request.
 *
 * CSS classes use the variant-selector BEM namespace (see variant-selector.css).
 */
(function () {
  'use strict';

  var EMBROIDERY_VARIANT_ID = 43495605647;
  var DEFAULT_QUANTITY_LIMIT = 8;
  var RESTRICTED_QUANTITY_LIMIT = 1;

  var GROUP_DEFS = [
    { key: 'DAD', label: 'Dad' },
    { key: 'MOM', label: 'Mom' },
    { key: 'ADULT', label: 'Adult' },
    { key: 'BOY', label: 'Boy' },
    { key: 'GIRL', label: 'Girl' },
    { key: 'BABY', label: 'Baby' },
    { key: 'CHILD', label: 'Child' },
    { key: 'KIDS', label: 'Kids' },
    { key: 'PET', label: 'Pet' },
  ];

  var SIZE_ORDER = [
    'XXXS', 'XXS', 'XS', 'S', 'SM', 'SMALL', 'M', 'MED', 'MEDIUM',
    'L', 'LG', 'LARGE', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL',
  ];

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ', '');
        break;
      default:
        value = defaultTo(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function normalize(value) {
    return value == null ? '' : String(value).trim().toLowerCase();
  }

  function detectGroupKey(value) {
    var upper = String(value).toUpperCase();
    for (var i = 0; i < GROUP_DEFS.length; i++) {
      if (upper.indexOf(GROUP_DEFS[i].key) !== -1) return GROUP_DEFS[i];
    }
    return null;
  }

  function cleanSizeLabel(value, groupKey) {
    var label = String(value).toUpperCase();
    if (groupKey) label = label.split(groupKey).join('');
    label = label.split('FAMILY').join('');
    label = label.replace(/^[\s-]+|[\s-]+$/g, '');
    label = label.replace(/\s*-\s*/g, ' - ');
    return label.trim() || String(value);
  }

  function sizeRank(label) {
    var token = String(label).toUpperCase().split(' - ')[0].replace(/\s/g, '');
    var index = SIZE_ORDER.indexOf(token);
    return index === -1 ? 999 : index;
  }

  function isTopSize(value) {
    var upper = String(value).toUpperCase();
    if (upper.indexOf('BOTTOM') !== -1) return false;
    return (
      upper.indexOf('MOM-TOP') !== -1 ||
      upper.indexOf('ADULT-TOP') !== -1 ||
      (upper.indexOf('-TOP') !== -1 && upper.indexOf('BOTTOM') === -1)
    );
  }

  function isBottomSize(value) {
    var upper = String(value).toUpperCase();
    if (upper.indexOf('TOP') !== -1 && upper.indexOf('BOTTOM') === -1) return false;
    return (
      upper.indexOf('MOM-BOTTOM') !== -1 ||
      upper.indexOf('ADULT-BOTTOM') !== -1 ||
      upper.indexOf('-BOTTOM') !== -1
    );
  }

  function groupElementId(uid, groupId) {
    return uid + '-group-' + groupId;
  }

  function VariantSelector(root) {
    var dataNode = root.querySelector('[data-variant-data]');
    if (!dataNode) {
      root.classList.remove('variant-selector--initial-loading');
      return;
    }

    try {
      this.data = JSON.parse(dataNode.textContent);
    } catch (error) {
      root.classList.remove('variant-selector--initial-loading');
      return;
    }

    this.root = root;
    this.uidPrefix = root.getAttribute('data-vs-uid') || 'variant-selector';
    this.quantityLimit = (this.data.isMysteryBox || this.data.hasEmbroidery || this.data.singleVariantLimit)
      ? RESTRICTED_QUANTITY_LIMIT
      : DEFAULT_QUANTITY_LIMIT;
    this.showSummaryPrice = this.data.showSummaryPrice === true;
    this.stockNotice = this.data.stockNotice || {};
    this.stockNoticeEnabled = this.stockNotice.enabled !== false;
    this.groupsContainer = root.querySelector('[data-vs-groups]');
    this.summaryEl = root.querySelector('[data-vs-summary]');
    this.priceEl = root.querySelector('[data-vs-price]');
    this.priceValueEl = root.querySelector('[data-vs-price-value]');
    this.footerEl = root.querySelector('.variant-selector__footer');
    this.setEl = root.querySelector('[data-vs-set]');
    this.setHeaderEl = root.querySelector('[data-vs-set-header]');
    this.setTitleEl = root.querySelector('[data-vs-set-title]');
    this.setSubtitleEl = root.querySelector('[data-vs-set-subtitle]');
    this.bikiniNoticeEl = root.querySelector('[data-vs-bikini-notice]');
    this.groupMessageEl = root.querySelector('[data-vs-group-message]');
    this.errorEl = root.querySelector('[data-vs-error]');
    this.availabilityEl = document.querySelector('[data-vs-availability]');
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.variants = this.data.variants || [];
    this.options = this.data.options || [];
    this.optionMetaByPosition = {};
    this.options.forEach(function (option) {
      var valueMeta = {};
      (option.values || []).forEach(function (meta) {
        valueMeta[meta.value] = meta;
      });
      this.optionMetaByPosition[option.position] = {
        isColor: !!option.isColor,
        valueMeta: valueMeta,
      };
    }.bind(this));
    this.productTitle = root.getAttribute('data-product-title') || '';
    this.emptySummaryText = root.getAttribute('data-empty-summary') || '0 piece selected';

    this.addButton = document.getElementById('addToCartAjax');
    this.addLabel = document.getElementById('addToCartLabel');
    this.oosLabel = document.getElementById('OOSLabel');

    this.productPagePriceEl = document.querySelector('.product-page__price');
    this.productPageCompareEl = document.querySelector('.product-compare-price');
    this.productPageInstallmentEl = document.querySelector('.product-page__installments .text-highlight');
    this.activeVariantId = null;

    this.cards = [];
    this.defaultQuantity = 1;
    this.isLoading = false;
    this.errorTimeout = null;

    this.buildModel();
    this.render();
    this.updateGroupMessage();
    this.bindAddToCart();
    this.initActiveVariant();
    this.refreshSummary();
    this.clearInitialLoading();
  }

  VariantSelector.prototype.clearInitialLoading = function () {
    if (this.root) {
      this.root.classList.remove('variant-selector--initial-loading');
      this.root.setAttribute('aria-busy', 'false');
    }
  };

  VariantSelector.prototype.quantityLimitMessage = function () {
    if (this.quantityLimit === 1) {
      return 'You can only add 1 item at a time.';
    }
    return 'You can add up to ' + this.quantityLimit + ' items at a time.';
  };

  VariantSelector.prototype.panelId = function (group) {
    return groupElementId(this.uidPrefix, group.id) + '-panel';
  };

  VariantSelector.prototype.titleId = function (group) {
    return groupElementId(this.uidPrefix, group.id) + '-title';
  };

  VariantSelector.prototype.swatchLabel = function (group, entry, value, available) {
    var display = entry.isGroupOption
      ? cleanSizeLabel(value, group.groupKey)
      : value;
    var parts = [];
    if (group.label) parts.push(group.label);
    if (entry.option && entry.option.name) parts.push(entry.option.name);
    parts.push(display);
    if (available === false) parts.push('sold out');
    return parts.join(', ');
  };

  VariantSelector.prototype.bindSwatchKeyboard = function (swatchesEl) {
    swatchesEl.addEventListener('keydown', function (event) {
      if (
        event.key !== 'ArrowRight' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowUp'
      ) {
        return;
      }

      var buttons = Array.prototype.slice.call(swatchesEl.querySelectorAll('button')).filter(function (btn) {
        return !btn.disabled;
      });
      if (!buttons.length) return;

      var currentIndex = buttons.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      var nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      }

      event.preventDefault();
      buttons[nextIndex].focus();
    });
  };

  VariantSelector.prototype.createOptionField = function (entry, parentEl, legendText) {
    var fieldset = document.createElement('fieldset');
    fieldset.className = 'variant-selector-option__fieldset';

    var legend = document.createElement('legend');
    legend.className = 'variant-selector-option__legend';
    legend.textContent = legendText;
    fieldset.appendChild(legend);

    var optionEl = document.createElement('div');
    optionEl.className = 'variant-selector-option';
    optionEl.setAttribute('data-option-position', entry.option.position);
    if (this.isColorOption(entry.option.position)) {
      optionEl.classList.add('variant-selector-option--color');
    }
    if (legendText && (entry.option.name !== legendText)) {
      optionEl.classList.add('variant-selector-option--split');
    }

    var swatches = document.createElement('div');
    swatches.className = 'variant-selector-option__swatches';
    swatches.setAttribute('role', 'group');
    swatches.setAttribute('aria-label', legendText);

    optionEl.appendChild(swatches);
    fieldset.appendChild(optionEl);
    parentEl.appendChild(fieldset);
    this.bindSwatchKeyboard(swatches);

    return { optionEl: optionEl, swatches: swatches };
  };

  VariantSelector.prototype.buildModel = function () {
    var self = this;

    this.valuesByPosition = {};
    this.options.forEach(function (option) {
      self.valuesByPosition[option.position] = [];
    });
    this.variants.forEach(function (variant) {
      variant.options.forEach(function (value, index) {
        var position = index + 1;
        var list = self.valuesByPosition[position] || (self.valuesByPosition[position] = []);
        if (list.indexOf(value) === -1) list.push(value);
      });
    });

    this.defaultVariant = null;
    if (this.variants.length === 1 && normalize(this.variants[0].options[0]) === 'default title') {
      this.defaultVariant = this.variants[0];
      this.groups = [];
      this.multiOptionPosition = null;
      return;
    }

    this.groupOption = null;
    for (var i = 0; i < this.options.length; i++) {
      var position = this.options[i].position;
      var values = this.valuesByPosition[position] || [];
      var hasKeyword = values.some(function (value) {
        return detectGroupKey(value) !== null;
      });
      if (hasKeyword) {
        this.groupOption = this.options[i];
        break;
      }
    }

    this.multiOptionPosition = this.groupOption
      ? this.groupOption.position
      : this.findSizeOptionPosition();

    this.groups = this.buildGroups();
    this.isSingleGroup = this.groups.length === 1;
    this.gradeSchoolLayout = this.isGradeSchoolProduct();
  };

  VariantSelector.prototype.isGradeSchoolProduct = function () {
    if (this.data.gradeSchoolLayout !== true) return false;
    if (this.groupOption) return false;
    if (!this.isSingleGroup) return false;
    if (this.defaultVariant) return false;
    if (this.options.length !== 2) return false;

    var hasType = false;
    var hasSize = false;
    this.options.forEach(function (option) {
      if (/^type$/i.test(option.name)) hasType = true;
      if (/size/i.test(option.name)) hasSize = true;
    });
    return hasType && hasSize;
  };

  VariantSelector.prototype.getTypeOptionPosition = function () {
    for (var i = 0; i < this.options.length; i++) {
      if (/^type$/i.test(this.options[i].name)) return this.options[i].position;
    }
    return null;
  };

  VariantSelector.prototype.hasTypeSelected = function (card) {
    var typePosition = this.getTypeOptionPosition();
    if (typePosition == null) return false;
    return card.sharedSelections[typePosition] != null;
  };

  VariantSelector.prototype.setGradeSizeStepExpanded = function (card, expanded) {
    var step = card.gradeSizeStep;
    if (!step) return;
    if (expanded && step.headerEl.disabled) return;

    step.expanded = expanded;
    step.stepEl.classList.toggle('variant-selector-grade-step--collapsed', !expanded);
    step.stepEl.classList.toggle('variant-selector-grade-step--expanded', expanded);
    step.headerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (step.bodyEl) {
      if ('inert' in step.bodyEl) {
        step.bodyEl.inert = !expanded;
      } else {
        step.bodyEl.querySelectorAll('button').forEach(function (control) {
          if (!expanded) {
            control.setAttribute('tabindex', '-1');
          } else {
            control.removeAttribute('tabindex');
          }
        });
      }
    }
  };

  VariantSelector.prototype.updateGradeSizeStepState = function (card) {
    var step = card.gradeSizeStep;
    if (!step) return;

    var hasType = this.hasTypeSelected(card);
    step.headerEl.disabled = !hasType;
    step.stepEl.classList.toggle('variant-selector-grade-step--locked', !hasType);

    if (!hasType) {
      this.setGradeSizeStepExpanded(card, false);
    }
  };

  VariantSelector.prototype.buildGradeSchoolOptions = function (card, group, cardOptions, optionsWrap) {
    var self = this;
    var labels = (this.data.gradeSchoolLabels) || {};
    var typeEntry = null;
    var sizeEntry = null;

    cardOptions.forEach(function (entry) {
      if (!entry.values.length) return;
      if (self.isMultiOption(entry.option.position)) {
        sizeEntry = entry;
      } else if (/^type$/i.test(entry.option.name)) {
        typeEntry = entry;
      }
    });

    if (!typeEntry || !sizeEntry) return;

    card.el.classList.add('variant-selector-group--grade-school');

    var stepsWrap = document.createElement('div');
    stepsWrap.className = 'variant-selector-grade-steps';

    var step1 = document.createElement('div');
    step1.className = 'variant-selector-grade-step variant-selector-grade-step--type';
    var step1Title = document.createElement('p');
    step1Title.className = 'variant-selector-grade-step__title';
    step1Title.textContent = labels.step1 || 'Step 1: Select a Type';
    step1.appendChild(step1Title);
    self.appendOptionSwatches(card, group, typeEntry, step1);
    stepsWrap.appendChild(step1);

    var sizeStepId = groupElementId(this.uidPrefix, group.id) + '-grade-size';
    var step2 = document.createElement('div');
    step2.className =
      'variant-selector-grade-step variant-selector-grade-step--size variant-selector-grade-step--collapsed variant-selector-grade-step--locked';

    var sizeHeader = document.createElement('button');
    sizeHeader.type = 'button';
    sizeHeader.className = 'variant-selector-grade-step__header';
    sizeHeader.setAttribute('aria-expanded', 'false');
    sizeHeader.setAttribute('aria-controls', sizeStepId + '-body');
    sizeHeader.disabled = true;
    sizeHeader.textContent = labels.step2 || 'Step 2: Select a Size';

    var sizeBody = document.createElement('div');
    sizeBody.id = sizeStepId + '-body';
    sizeBody.className = 'variant-selector-grade-step__body';
    if ('inert' in sizeBody) {
      sizeBody.inert = true;
    }

    var sizeBodyInner = document.createElement('div');
    sizeBodyInner.className = 'variant-selector-grade-step__body-inner';
    self.appendOptionSwatches(card, group, sizeEntry, sizeBodyInner);

    sizeBody.appendChild(sizeBodyInner);
    step2.appendChild(sizeHeader);
    step2.appendChild(sizeBody);

    card.gradeSizeStep = {
      stepEl: step2,
      headerEl: sizeHeader,
      bodyEl: sizeBody,
      expanded: false,
    };

    sizeHeader.addEventListener('click', function () {
      if (sizeHeader.disabled) return;
      self.setGradeSizeStepExpanded(card, !card.gradeSizeStep.expanded);
    });

    stepsWrap.appendChild(step2);
    optionsWrap.appendChild(stepsWrap);
    this.updateGradeSizeStepState(card);
  };

  VariantSelector.prototype.appendOptionSwatches = function (card, group, entry, parentEl) {
    var self = this;
    var field = this.createOptionField(entry, parentEl, entry.option.name);
    var swatches = field.swatches;
    var buttons = [];

    entry.values.forEach(function (value) {
      var button = self.createSwatchButton(card, group, entry, value);
      swatches.appendChild(button);
      buttons.push(button);
    });

    card.buttonsByPosition[entry.option.position] = buttons;
    return field;
  };

  VariantSelector.prototype.findSizeOptionPosition = function () {
    for (var i = 0; i < this.options.length; i++) {
      if (/size/i.test(this.options[i].name)) return this.options[i].position;
    }
    return this.options.length ? this.options[this.options.length - 1].position : null;
  };

  VariantSelector.prototype.isMultiOption = function (position) {
    return this.multiOptionPosition != null && position === this.multiOptionPosition;
  };

  VariantSelector.prototype.buildGroups = function () {
    var self = this;

    if (!this.groupOption) {
      return [{ id: 'all', label: '', groupKey: null, sizeValues: null }];
    }

    var position = this.groupOption.position;
    var values = this.valuesByPosition[position] || [];
    var buckets = {};
    var order = [];

    values.forEach(function (value) {
      var def = detectGroupKey(value);
      var key = def ? def.key : 'OTHER';
      var label = def ? def.label : self.groupOption.name;
      if (!buckets[key]) {
        buckets[key] = { id: key.toLowerCase(), label: label, groupKey: def ? def.key : null, sizeValues: [] };
        order.push(key);
      }
      buckets[key].sizeValues.push(value);
    });

    var ordered = [];
    GROUP_DEFS.forEach(function (def) {
      if (buckets[def.key]) ordered.push(buckets[def.key]);
    });
    order.forEach(function (key) {
      if (key === 'OTHER' && buckets[key]) ordered.push(buckets[key]);
    });

    return ordered;
  };

  VariantSelector.prototype.variantsForGroup = function (group) {
    if (!group.sizeValues || !this.groupOption) return this.variants.slice();
    var position = this.groupOption.position;
    var allowed = group.sizeValues.map(normalize);
    return this.variants.filter(function (variant) {
      return allowed.indexOf(normalize(variant.options[position - 1])) !== -1;
    });
  };

  VariantSelector.prototype.cardOptions = function (group) {
    var self = this;
    return this.options.map(function (option) {
      var isGroupOption = self.groupOption && option.position === self.groupOption.position;
      var values;
      if (isGroupOption && group.sizeValues) {
        values = group.sizeValues.slice();
      } else {
        values = (self.valuesByPosition[option.position] || []).slice();
      }
      return { option: option, isGroupOption: isGroupOption, values: values };
    });
  };

  VariantSelector.prototype.shouldSplitTopsBottoms = function (group, values) {
    if (!group.groupKey || (group.groupKey !== 'MOM' && group.groupKey !== 'ADULT')) {
      return false;
    }
    var hasTops = false;
    var hasBottoms = false;
    values.forEach(function (value) {
      if (isTopSize(value)) hasTops = true;
      if (isBottomSize(value)) hasBottoms = true;
    });
    return hasTops && hasBottoms;
  };

  VariantSelector.prototype.isFamilySet = function () {
    if (!this.groupOption || this.groups.length < 4) return false;
    if (this.data && this.data.isMysteryBox) return false;
    return true;
  };

  VariantSelector.prototype.isGroupActive = function (card) {
    if (this.isSingleGroup) return true;
    return card.el.classList.contains('variant-selector-group--expanded');
  };

  VariantSelector.prototype.clearGroupSelections = function (card) {
    card.quantityByValue = {};
    card.sharedSelections = {};
  };

  VariantSelector.prototype.getMissingSharedSelectionMessage = function (card) {
    var self = this;
    var missingOption = null;

    Object.keys(card.buttonsByPosition).some(function (positionKey) {
      var position = Number(positionKey);
      if (self.isMultiOption(position)) return false;
      if (card.sharedSelections[position] == null) {
        missingOption = self.options.filter(function (option) {
          return option.position === position;
        })[0];
        return true;
      }
      return false;
    });

    if (!missingOption) return 'Select all options first!';
    return 'Select a ' + missingOption.name.toLowerCase() + ' first!';
  };

  VariantSelector.prototype.hasRequiredSharedSelections = function (card) {
    var self = this;
    return Object.keys(card.buttonsByPosition).every(function (positionKey) {
      var position = Number(positionKey);
      if (self.isMultiOption(position)) return true;
      return card.sharedSelections[position] != null;
    });
  };

  VariantSelector.prototype.getGroupMessage = function () {
    var messages = (this.data && this.data.messages) || {};

    if (this.data && this.data.isMysteryBox) {
      return messages.mysteryBox || '';
    }

    if (this.quantityLimit === RESTRICTED_QUANTITY_LIMIT) {
      return '';
    }

    if (!this.groups.length) return '';

    var hasPetGroup = this.groups.some(function (group) {
      return group.groupKey === 'PET' || group.id === 'pet';
    });
    if (hasPetGroup) {
      return messages.pet || '';
    }

    if (this.groupOption && this.groups.length >= 2) {
      return messages.multiGroup || '';
    }

    return messages.singleGroup || '';
  };

  VariantSelector.prototype.updateGroupMessage = function () {
    var messages = (this.data && this.data.messages) || {};
    var isFamilySet = this.isFamilySet();

    if (this.setEl) {
      this.setEl.classList.toggle('variant-selector__set--family', isFamilySet);
    }

    if (isFamilySet) {
      var count = this.groups.length;

      if (this.setHeaderEl) this.setHeaderEl.hidden = false;
      if (this.setTitleEl) {
        this.setTitleEl.textContent = messages.matchingSetTitle || 'Build your matching set';
      }
      if (this.setSubtitleEl) {
        var memberCount =
          count + (count === 1 ? ' family member' : ' family members');
        this.setSubtitleEl.textContent =
          this.quantityLimit === RESTRICTED_QUANTITY_LIMIT
            ? memberCount
            : memberCount + ' · tap multiple sizes for siblings';
      }
      if (this.bikiniNoticeEl) {
        this.bikiniNoticeEl.hidden = !this.bikiniNoticeEl.textContent.trim();
      }
      if (this.groupMessageEl) {
        var familyMessage = this.getGroupMessage();
        this.groupMessageEl.textContent = familyMessage;
        this.groupMessageEl.hidden = !familyMessage;
      }
      return;
    }

    if (this.setHeaderEl) this.setHeaderEl.hidden = true;
    if (this.bikiniNoticeEl) {
      this.bikiniNoticeEl.hidden = !this.bikiniNoticeEl.textContent.trim();
    }

    if (!this.groupMessageEl) return;

    var message = this.getGroupMessage();
    if (message) {
      this.groupMessageEl.textContent = message;
      this.groupMessageEl.hidden = false;
    } else {
      this.groupMessageEl.textContent = '';
      this.groupMessageEl.hidden = true;
    }
  };

  VariantSelector.prototype.isColorOption = function (position) {
    var meta = this.optionMetaByPosition[position];
    return !!(meta && meta.isColor);
  };

  VariantSelector.prototype.createSwatchButton = function (card, group, entry, value) {
    var self = this;
    var optionMeta = this.optionMetaByPosition[entry.option.position];
    var button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-value', value);
    button.setAttribute('data-option-position', String(entry.option.position));
    button.setAttribute('aria-pressed', 'false');

    if (optionMeta && optionMeta.isColor) {
      var valueMeta = optionMeta.valueMeta[value] || {};
      button.className = 'variant-selector-color-swatch';

      var chip = document.createElement('span');
      chip.className = 'variant-selector-color-swatch__chip';
      chip.setAttribute('aria-hidden', 'true');
      if (valueMeta.backgroundColor) {
        chip.style.backgroundColor = valueMeta.backgroundColor;
      }
      if (valueMeta.backgroundImage) {
        chip.style.backgroundImage = 'url(' + valueMeta.backgroundImage + ')';
      }
      button.appendChild(chip);
    } else {
      button.className = 'variant-selector-swatch';
      button.textContent = entry.isGroupOption
        ? cleanSizeLabel(value, group.groupKey)
        : value;
    }

    button.setAttribute('aria-label', this.swatchLabel(group, entry, value, true));

    button.addEventListener('click', function () {
      self.onSelect(card, entry.option.position, value);
    });

    return button;
  };

  VariantSelector.prototype.appendSwatches = function (card, group, entry, values, parentEl, headingText) {
    var self = this;
    var legendText = headingText || entry.option.name;
    var field = this.createOptionField(entry, parentEl, legendText);
    var swatches = field.swatches;

    if (!card.buttonsByPosition[entry.option.position]) {
      card.buttonsByPosition[entry.option.position] = [];
    }

    values.forEach(function (value) {
      var button = self.createSwatchButton(card, group, entry, value);
      swatches.appendChild(button);
      card.buttonsByPosition[entry.option.position].push(button);
    });
  };

  VariantSelector.prototype.render = function () {
    var self = this;
    if (!this.groupsContainer) return;
    this.groupsContainer.innerHTML = '';

    this.groups.forEach(function (group) {
      var card = self.buildCard(group);
      self.cards.push(card);
      self.groupsContainer.appendChild(card.el);
    });

    if (this.gradeSchoolLayout && this.root) {
      this.root.classList.add('variant-selector--grade-school');
    }

    this.groupsContainer.setAttribute('data-group-count', String(this.cards.length));
  };

  VariantSelector.prototype.buildCard = function (group) {
    var self = this;
    var groupVariants = this.variantsForGroup(group);
    var cardOptions = this.cardOptions(group);
    var isSingleGroup = this.isSingleGroup;

    var el = document.createElement('div');
    el.className = 'variant-selector-group' + (isSingleGroup ? ' variant-selector-group--single' : '');
    el.setAttribute('data-group-id', group.id);

    var header = null;
    var price = null;
    var subtitle = null;
    var badge = null;
    var defaultSubtitle = this.groupSubtitle(group);
    var panelId = this.panelId(group);
    var titleId = this.titleId(group);

    if (!isSingleGroup) {
      header = document.createElement('button');
      header.type = 'button';
      header.className = 'variant-selector-group__header';
      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('aria-controls', panelId);

      var checkboxVisual = document.createElement('span');
      checkboxVisual.className = 'variant-selector-group__checkbox';
      checkboxVisual.setAttribute('aria-hidden', 'true');

      var heading = document.createElement('span');
      heading.className = 'variant-selector-group__heading';

      var titleRow = document.createElement('span');
      titleRow.className = 'variant-selector-group__title-row';

      var title = document.createElement('span');
      title.className = 'variant-selector-group__title';
      title.id = titleId;
      title.textContent = group.label;
      titleRow.appendChild(title);

      heading.appendChild(titleRow);

      subtitle = document.createElement('span');
      subtitle.className = 'variant-selector-group__subtitle';
      subtitle.setAttribute('aria-live', 'polite');
      subtitle.textContent = defaultSubtitle;
      heading.appendChild(subtitle);

      var meta = document.createElement('span');
      meta.className = 'variant-selector-group__meta';

      badge = document.createElement('span');
      badge.className = 'variant-selector-group__badge';
      badge.setAttribute('aria-live', 'polite');
      badge.hidden = true;

      price = document.createElement('span');
      price.className = 'variant-selector-group__price';
      price.setAttribute('aria-live', 'polite');
      meta.appendChild(price);
      meta.appendChild(badge);

      header.appendChild(checkboxVisual);
      header.appendChild(heading);
      header.appendChild(meta);
      el.appendChild(header);
    }

    var body = document.createElement('div');
    body.className = 'variant-selector-group__body';
    body.id = panelId;
    body.setAttribute('role', 'region');
    if (isSingleGroup) {
      body.setAttribute('aria-label', group.label || 'Product options');
    } else {
      body.setAttribute('aria-labelledby', titleId);
    }
    if (isSingleGroup) {
      body.hidden = false;
      body.setAttribute('aria-hidden', 'false');
    } else {
      body.setAttribute('aria-hidden', 'true');
    }

    var bodyInner = document.createElement('div');
    bodyInner.className = 'variant-selector-group__body-inner';

    var optionsWrap = document.createElement('div');
    optionsWrap.className = 'variant-selector-group__options';
    bodyInner.appendChild(optionsWrap);

    var quantitiesWrap = document.createElement('div');
    quantitiesWrap.className = 'variant-selector-group__quantities';
    bodyInner.appendChild(quantitiesWrap);

    body.appendChild(bodyInner);
    el.appendChild(body);

    var card = {
      el: el,
      group: group,
      groupVariants: groupVariants,
      priceEl: price,
      subtitleEl: subtitle,
      badgeEl: badge,
      defaultSubtitle: defaultSubtitle,
      headerEl: isSingleGroup ? null : header,
      bodyEl: body,
      quantitiesEl: quantitiesWrap,
      sharedSelections: {},
      quantityByValue: {},
      buttonsByPosition: {},
    };

    if (card.headerEl) {
      card.headerEl.addEventListener('click', function () {
        self.toggleCardExpanded(card);
      });
    }

    if (this.gradeSchoolLayout && isSingleGroup) {
      this.buildGradeSchoolOptions(card, group, cardOptions, optionsWrap);
    } else {
      cardOptions.forEach(function (entry) {
        if (!entry.values.length) return;

        if (entry.isGroupOption && self.shouldSplitTopsBottoms(group, entry.values)) {
          var topValues = entry.values.filter(isTopSize);
          var bottomValues = entry.values.filter(isBottomSize);
          self.appendSwatches(
            card,
            group,
            entry,
            topValues,
            optionsWrap,
            self.data.swimsuitTopSubheading || 'TOPS ONLY'
          );
          self.appendSwatches(
            card,
            group,
            entry,
            bottomValues,
            optionsWrap,
            self.data.swimsuitBottomSubheading || 'BOTTOMS ONLY'
          );
          return;
        }

        self.appendOptionSwatches(card, group, entry, optionsWrap);
      });
    }

    this.updateAvailability(card);
    this.updateCardState(card);
    if (!isSingleGroup) {
      this.setCardExpanded(card, false);
    }
    return card;
  };

  VariantSelector.prototype.toggleCardExpanded = function (card) {
    if (this.isSingleGroup) return;
    var expanded = !card.el.classList.contains('variant-selector-group--expanded');
    this.setCardExpanded(card, expanded);
    if (expanded) {
      this.updateAvailability(card);
    }
    this.refreshSummary();
  };

  VariantSelector.prototype.setCardExpanded = function (card, expanded) {
    if (this.isSingleGroup) return;
    if (!expanded) {
      this.clearGroupSelections(card);
    }
    if (card.headerEl) {
      card.headerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (card.bodyEl) {
      card.bodyEl.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      if ('inert' in card.bodyEl) {
        card.bodyEl.inert = !expanded;
      } else {
        card.bodyEl.querySelectorAll('button').forEach(function (control) {
          if (!expanded) {
            control.setAttribute('tabindex', '-1');
          } else {
            control.removeAttribute('tabindex');
          }
        });
      }
    }
    card.el.classList.toggle('variant-selector-group--expanded', expanded);
  };

  VariantSelector.prototype.groupSubtitle = function (group) {
    if (!group.sizeValues || !group.sizeValues.length) return '';
    var labels = group.sizeValues.map(function (value) {
      return cleanSizeLabel(value, group.groupKey);
    });
    labels.sort(function (a, b) {
      return sizeRank(a) - sizeRank(b);
    });
    if (labels.length === 1) return labels[0];
    return labels[0] + ' - ' + labels[labels.length - 1];
  };

  VariantSelector.prototype.onSelect = function (card, position, value) {
    if (this.isMultiOption(position)) {
      this.toggleSizeSelection(card, value);
      return;
    }

    if (this.data.singleVariantLimit) {
      card.quantityByValue = {};
    }

    if (card.sharedSelections[position] === value) {
      delete card.sharedSelections[position];
    } else {
      card.sharedSelections[position] = value;
    }

    this.pruneInvalidSelections(card);
    this.updateAvailability(card);

    if (this.gradeSchoolLayout && card.gradeSizeStep) {
      this.updateGradeSizeStepState(card);
      if (!this.isMultiOption(position) && card.sharedSelections[position]) {
        this.setGradeSizeStepExpanded(card, true);
      }
    }

    var variant = this.resolvePreviewVariant(card);
    if (variant) {
      this.trackActiveVariant(variant);
    } else {
      this.syncActiveVariant();
    }

    this.refreshSummary();

    if (variant && variant.image) {
      this.switchGalleryImage(variant.image);
      this.updateVariantUrl(variant.id);
    }
  };

  VariantSelector.prototype.toggleSizeSelection = function (card, value) {
    if (card.quantityByValue[value] != null) {
      delete card.quantityByValue[value];
    } else {
      if (!this.hasRequiredSharedSelections(card)) {
        this.showError(this.getMissingSharedSelectionMessage(card));
        return;
      }
      if (this.data.singleVariantLimit) {
        card.quantityByValue = {};
      } else if (this.totalQuantity() >= this.quantityLimit) {
        this.showError(this.quantityLimitMessage());
        return;
      }
      card.quantityByValue[value] = 1;
    }

    this.updateAvailability(card);

    var variant = card.quantityByValue[value] != null
      ? this.resolveVariantForValue(card, value)
      : null;
    if (variant) {
      this.trackActiveVariant(variant);
    } else {
      var preview = this.resolvePreviewVariant(card);
      if (preview) this.trackActiveVariant(preview);
      else this.syncActiveVariant();
    }

    this.refreshSummary();

    if (variant && variant.image) {
      this.switchGalleryImage(variant.image);
      this.updateVariantUrl(variant.id);
    }
  };

  VariantSelector.prototype.pruneInvalidSelections = function (card) {
    var self = this;
    Object.keys(card.quantityByValue).forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (!variant || !variant.available) {
        delete card.quantityByValue[value];
      }
    });
  };

  VariantSelector.prototype.buildSelections = function (card, multiValue) {
    var selections = {};

    Object.keys(card.sharedSelections).forEach(function (positionKey) {
      selections[Number(positionKey)] = card.sharedSelections[positionKey];
    });

    if (multiValue != null && this.multiOptionPosition != null) {
      selections[this.multiOptionPosition] = multiValue;
    }

    return selections;
  };

  VariantSelector.prototype.resolveVariantForValue = function (card, multiValue) {
    if (multiValue == null) return null;

    var selections = this.buildSelections(card, multiValue);
    var positions = Object.keys(card.buttonsByPosition).map(Number);
    var complete = positions.every(function (position) {
      return selections[position] != null;
    });
    if (!complete) return null;

    var matches = card.groupVariants.filter(function (variant) {
      return positions.every(function (position) {
        return normalize(variant.options[position - 1]) === normalize(selections[position]);
      });
    });

    if (!matches.length) return null;
    var available = matches.filter(function (variant) {
      return variant.available;
    });
    return available[0] || matches[0];
  };

  VariantSelector.prototype.resolvePreviewVariant = function (card) {
    var readyItems = this.cardReadyItems(card);
    if (readyItems.length) return readyItems[0].variant;

    var pool = this.getDisplayPricePool(card);
    if (pool.length === 1) return pool[0];
    return null;
  };

  VariantSelector.prototype.updateAvailability = function (card) {
    var self = this;
    Object.keys(card.buttonsByPosition).forEach(function (positionKey) {
      var position = Number(positionKey);
      var isMulti = self.isMultiOption(position);

      card.buttonsByPosition[position].forEach(function (button) {
        var value = button.getAttribute('data-value');
        var probeSelections = isMulti
          ? self.buildSelections(card, value)
          : Object.assign({}, card.sharedSelections, (function () {
              var next = {};
              next[position] = value;
              return next;
            })());

        var available = card.groupVariants.some(function (variant) {
          if (!variant.available) return false;
          return Object.keys(probeSelections).every(function (selKey) {
            var selPosition = Number(selKey);
            return normalize(variant.options[selPosition - 1]) === normalize(probeSelections[selPosition]);
          });
        });

        var entry = {
          option: self.options.filter(function (opt) {
            return opt.position === position;
          })[0],
          isGroupOption: isMulti,
        };

        var soldoutClass = button.classList.contains('variant-selector-color-swatch')
          ? 'variant-selector-color-swatch--soldout'
          : 'variant-selector-swatch--soldout';
        button.classList.toggle(soldoutClass, !available);
        button.disabled = !available;
        button.setAttribute('aria-label', self.swatchLabel(card.group, entry, value, available));
      });
    });
  };

  VariantSelector.prototype.getSelectedMultiValues = function (card) {
    return Object.keys(card.quantityByValue).sort(function (a, b) {
      var labelA = this.groupOption
        ? cleanSizeLabel(a, card.group.groupKey)
        : a;
      var labelB = this.groupOption
        ? cleanSizeLabel(b, card.group.groupKey)
        : b;
      return sizeRank(labelA) - sizeRank(labelB);
    }.bind(this));
  };

  VariantSelector.prototype.renderQuantityRows = function (card) {
    var self = this;
    card.quantitiesEl.innerHTML = '';

    this.getSelectedMultiValues(card).forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (!variant) return;

      var label = self.groupOption
        ? cleanSizeLabel(value, card.group.groupKey)
        : value;

      var row = document.createElement('div');
      row.className = 'variant-selector-qty-row';
      row.setAttribute('data-value', value);

      var rowLabel = document.createElement('span');
      rowLabel.className = 'variant-selector-qty-row__label';
      rowLabel.textContent = 'Size: ' + label;
      row.appendChild(rowLabel);

      var qty = document.createElement('div');
      qty.className = 'variant-selector-qty';
      qty.setAttribute('role', 'group');
      qty.setAttribute('aria-label', 'Quantity for ' + label);

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'variant-selector-qty__btn';
      minus.setAttribute('aria-label', 'Decrease quantity for ' + label);
      minus.textContent = '−';
      minus.addEventListener('click', function () {
        self.changeQuantity(card, value, -1);
      });

      var count = document.createElement('span');
      count.className = 'variant-selector-qty__count';
      count.setAttribute('aria-live', 'polite');
      count.setAttribute('aria-atomic', 'true');
      count.textContent = String(card.quantityByValue[value]);

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'variant-selector-qty__btn';
      plus.setAttribute('aria-label', 'Increase quantity for ' + label);
      plus.textContent = '+';
      plus.addEventListener('click', function () {
        self.changeQuantity(card, value, 1);
      });
      plus.disabled = self.totalQuantity() >= self.quantityLimit;

      qty.appendChild(minus);
      qty.appendChild(count);
      qty.appendChild(plus);
      row.appendChild(qty);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'variant-selector-qty-row__remove';
      remove.setAttribute('aria-label', 'Remove ' + label);
      remove.textContent = '×';
      remove.addEventListener('click', function () {
        delete card.quantityByValue[value];
        self.updateAvailability(card);
        self.refreshSummary();
      });
      row.appendChild(remove);

      card.quantitiesEl.appendChild(row);
    });
  };

  VariantSelector.prototype.changeQuantity = function (card, value, delta) {
    var current = card.quantityByValue[value] || 0;
    var next = current + delta;

    if (next <= 0) {
      delete card.quantityByValue[value];
    } else if (delta > 0 && this.totalQuantity() >= this.quantityLimit) {
      this.showError(this.quantityLimitMessage());
      return;
    } else {
      card.quantityByValue[value] = next;
    }

    var activeVariant = card.quantityByValue[value] != null
      ? this.resolveVariantForValue(card, value)
      : null;
    if (activeVariant) {
      this.trackActiveVariant(activeVariant);
    } else {
      this.syncActiveVariant();
    }

    this.refreshSummary();
  };

  VariantSelector.prototype.cardItemCount = function (card) {
    var total = 0;
    Object.keys(card.quantityByValue).forEach(function (value) {
      total += card.quantityByValue[value];
    });
    return total;
  };

  VariantSelector.prototype.cardReadyItems = function (card) {
    if (!this.isGroupActive(card)) return [];

    var self = this;
    var items = [];

    Object.keys(card.quantityByValue).forEach(function (value) {
      var variant = self.resolveVariantForValue(card, value);
      if (variant && variant.available) {
        items.push({ variant: variant, quantity: card.quantityByValue[value] });
      }
    });

    return items;
  };

  VariantSelector.prototype.updateCardState = function (card) {
    var self = this;

    Object.keys(card.buttonsByPosition).forEach(function (positionKey) {
      var position = Number(positionKey);
      card.buttonsByPosition[position].forEach(function (button) {
        var value = button.getAttribute('data-value');
        var isActive = self.isMultiOption(position)
          ? card.quantityByValue[value] != null
          : card.sharedSelections[position] === value;
        var activeClass = button.classList.contains('variant-selector-color-swatch')
          ? 'variant-selector-color-swatch--active'
          : 'variant-selector-swatch--active';
        button.classList.toggle(activeClass, isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });

    this.renderQuantityRows(card);

    if (this.gradeSchoolLayout) {
      this.updateGradeSizeStepState(card);
    }

    var readyItems = this.cardReadyItems(card);

    if (card.priceEl) {
      card.priceEl.textContent = this.groupDisplayPrice(card, readyItems);
    }

    if (card.subtitleEl) {
      if (readyItems.length) {
        card.subtitleEl.textContent = readyItems
          .map(function (item) {
            var multiValue = item.variant.options[self.multiOptionPosition - 1];
            var label = self.groupOption
              ? cleanSizeLabel(multiValue, card.group.groupKey)
              : multiValue;
            return item.quantity > 1 ? label + ' ×' + item.quantity : label;
          })
          .join(', ');
      } else {
        var selectedMultiValues = self.getSelectedMultiValues(card);
        if (selectedMultiValues.length) {
          card.subtitleEl.textContent = selectedMultiValues
            .map(function (value) {
              var label = self.groupOption
                ? cleanSizeLabel(value, card.group.groupKey)
                : value;
              var qty = card.quantityByValue[value];
              return qty > 1 ? label + ' ×' + qty : label;
            })
            .join(', ');
        } else {
          card.subtitleEl.textContent = card.defaultSubtitle;
        }
      }
    }

    if (card.badgeEl) {
      var selectedCount = self.cardItemCount(card);
      if (selectedCount > 0) {
        card.badgeEl.hidden = false;
        card.badgeEl.textContent = selectedCount + (selectedCount === 1 ? ' item' : ' items');
      } else {
        card.badgeEl.hidden = true;
        card.badgeEl.textContent = '';
      }
    }

    var isSelected = self.cardItemCount(card) > 0;
    card.el.classList.toggle('variant-selector-group--selected', isSelected);
  };

  VariantSelector.prototype.getDisplayPricePool = function (card) {
    var pool = card.groupVariants.filter(function (variant) {
      return variant.available;
    });
    if (!pool.length) pool = card.groupVariants.slice();
    if (!pool.length) return [];

    var selections = {};
    Object.keys(card.sharedSelections).forEach(function (positionKey) {
      selections[Number(positionKey)] = card.sharedSelections[positionKey];
    });

    if (!Object.keys(selections).length) return pool;

    return pool.filter(function (variant) {
      return Object.keys(selections).every(function (positionKey) {
        var position = Number(positionKey);
        return normalize(variant.options[position - 1]) === normalize(selections[position]);
      });
    });
  };

  VariantSelector.prototype.fromPriceCents = function (card) {
    var pool = this.getDisplayPricePool(card);
    if (!pool.length) return null;
    return pool.reduce(function (lowest, variant) {
      return variant.priceCents < lowest ? variant.priceCents : lowest;
    }, pool[0].priceCents);
  };

  VariantSelector.prototype.getDefaultPriceCents = function () {
    var available = this.variants.filter(function (variant) {
      return variant.available;
    });
    var pool = available.length ? available : this.variants;
    if (!pool.length) return null;
    return pool.reduce(function (lowest, variant) {
      return variant.priceCents < lowest ? variant.priceCents : lowest;
    }, pool[0].priceCents);
  };

  VariantSelector.prototype.trackActiveVariant = function (variant) {
    if (variant) this.activeVariantId = variant.id;
  };

  VariantSelector.prototype.initActiveVariant = function () {
    var initialId = this.getInitialVariantId();
    if (!initialId) return;

    var variant = this.findVariantById(initialId);
    if (variant) this.activeVariantId = variant.id;
  };

  VariantSelector.prototype.isVariantInCurrentSelection = function (variant) {
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return Object.keys(card.quantityByValue).some(function (value) {
        var resolved = self.resolveVariantForValue(card, value);
        return resolved && resolved.id === variant.id;
      });
    });
  };

  VariantSelector.prototype.findVariantById = function (variantId) {
    var id = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  VariantSelector.prototype.syncActiveVariant = function () {
    var active = this.getActiveVariant();
    this.activeVariantId = active ? active.id : null;
  };

  VariantSelector.prototype.hasResolvedSelection = function () {
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return (
        Object.keys(card.sharedSelections).length > 0 ||
        Object.keys(card.quantityByValue).length > 0
      );
    });
  };

  VariantSelector.prototype.getInitialVariantId = function () {
    var params = new URLSearchParams(window.location.search);
    var urlVariantId = parseInt(params.get('variant'), 10);
    if (urlVariantId && this.findVariantById(urlVariantId)) return urlVariantId;
    if (this.data.initialVariantId && this.findVariantById(this.data.initialVariantId)) {
      return this.data.initialVariantId;
    }
    return null;
  };

  VariantSelector.prototype.getActiveVariant = function () {
    if (this.hasResolvedSelection()) {
      if (this.activeVariantId) {
        var tracked = this.findVariantById(this.activeVariantId);
        if (tracked && this.isVariantInCurrentSelection(tracked)) return tracked;
      }

      var ready = this.readyItems();
      if (ready.length) return ready[ready.length - 1].variant;

      for (var i = this.cards.length - 1; i >= 0; i--) {
        var card = this.cards[i];
        if (!this.isGroupActive(card)) continue;
        var preview = this.resolvePreviewVariant(card);
        if (preview) return preview;
      }

      return null;
    }

    var initialId = this.getInitialVariantId();
    if (initialId) return this.findVariantById(initialId);

    return null;
  };

  VariantSelector.prototype.getProductPagePriceCents = function () {
    var variant = this.getActiveVariant();
    if (variant) return variant.priceCents;
    return this.getDefaultPriceCents();
  };

  VariantSelector.prototype.getProductPageCompareAtCents = function (priceCents) {
    var variant = this.getActiveVariant();
    if (!variant || variant.compareAtCents <= variant.priceCents) return null;
    return variant.compareAtCents;
  };

  VariantSelector.prototype.setProductPagePriceText = function (el, srOnlyLabel, formattedPrice) {
    if (!el) return;
    var srOnly = el.querySelector('.sr-only');
    el.textContent = '';
    if (srOnly) {
      el.appendChild(srOnly);
    } else if (srOnlyLabel) {
      var sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.textContent = srOnlyLabel;
      el.appendChild(sr);
    }
    el.appendChild(document.createTextNode(formattedPrice));
  };

  VariantSelector.prototype.updateProductPagePrice = function () {
    if (!this.productPagePriceEl) return;

    var priceCents = this.getProductPagePriceCents();
    if (priceCents == null) return;

    this.setProductPagePriceText(
      this.productPagePriceEl,
      'Price: ',
      formatMoney(priceCents, this.moneyFormat)
    );

    var compareAtCents = this.getProductPageCompareAtCents(priceCents);
    if (this.productPageCompareEl) {
      if (compareAtCents != null && compareAtCents > priceCents) {
        this.productPageCompareEl.hidden = false;
        this.setProductPagePriceText(
          this.productPageCompareEl,
          'Regular price: ',
          formatMoney(compareAtCents, this.moneyFormat)
        );
      } else {
        this.productPageCompareEl.textContent = '';
        this.productPageCompareEl.hidden = true;
      }
    }

    if (this.productPageInstallmentEl) {
      this.productPageInstallmentEl.textContent = formatMoney(
        Math.floor(priceCents / 4),
        this.moneyFormat
      );
    }
  };

  VariantSelector.prototype.fromPrice = function (card) {
    var cents = this.fromPriceCents(card);
    return cents == null ? '' : formatMoney(cents, this.moneyFormat);
  };

  VariantSelector.prototype.cartQuantity = function () {
    return this.readyItems().reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
  };

  VariantSelector.prototype.groupDisplayPrice = function (card, readyItems) {
    if (readyItems.length) {
      var totalCents = readyItems.reduce(function (sum, item) {
        return sum + item.variant.priceCents * item.quantity;
      }, 0);
      return formatMoney(totalCents, this.moneyFormat);
    }

    var pool = this.getDisplayPricePool(card);
    if (pool.length === 1) {
      return formatMoney(pool[0].priceCents, this.moneyFormat);
    }

    return this.fromPrice(card);
  };

  VariantSelector.prototype.totalQuantity = function () {
    var total = this.defaultVariant ? this.defaultQuantity : 0;
    var self = this;
    this.cards.forEach(function (card) {
      if (self.isGroupActive(card)) {
        total += self.cardItemCount(card);
      }
    });
    return total;
  };

  VariantSelector.prototype.readyItems = function () {
    if (this.defaultVariant) {
      return this.defaultVariant.available
        ? [{ variant: this.defaultVariant, quantity: this.defaultQuantity }]
        : [];
    }

    var self = this;
    var all = [];
    this.cards.forEach(function (card) {
      all = all.concat(self.cardReadyItems(card));
    });
    return all;
  };

  VariantSelector.prototype.selectedGroupCount = function () {
    var self = this;
    return this.cards.filter(function (card) {
      return self.isGroupActive(card) && self.cardItemCount(card) > 0;
    }).length;
  };

  VariantSelector.prototype.selectedQuantity = function () {
    if (this.defaultVariant) return this.defaultQuantity;
    return this.totalQuantity();
  };

  VariantSelector.prototype.formatSelectionSummary = function () {
    var quantity = this.selectedQuantity();
    if (!quantity) return this.emptySummaryText;

    var pieceText = quantity === 1 ? '1 piece' : quantity + ' pieces';

    if (!this.groupOption || this.groups.length <= 1) {
      return pieceText;
    }

    var groupCount = this.selectedGroupCount();
    var memberText = groupCount === 1 ? '1 family member' : groupCount + ' family members';
    return pieceText + ' · ' + memberText;
  };

  VariantSelector.prototype.isProductOutOfStock = function () {
    if (this.defaultVariant) {
      return !this.defaultVariant.available;
    }
    if (!this.variants.length) return true;
    return !this.variants.some(function (variant) {
      return variant.available;
    });
  };

  VariantSelector.prototype.hasUnavailableCompleteSelection = function () {
    var self = this;
    return this.cards.some(function (card) {
      if (!self.isGroupActive(card)) return false;
      return Object.keys(card.quantityByValue).some(function (value) {
        var variant = self.resolveVariantForValue(card, value);
        return variant && !variant.available;
      });
    });
  };

  VariantSelector.prototype.updateStockNotice = function () {
    if (!this.availabilityEl) return;

    if (!this.stockNoticeEnabled) {
      this.availabilityEl.hidden = true;
      return;
    }

    var variant = this.getActiveVariant();
    if (!variant) {
      this.availabilityEl.hidden = true;
      return;
    }

    var available = !!variant.available;
    var availableText = this.stockNotice.availableText || 'In Stock';
    var oosText = this.stockNotice.outOfStockText || 'Out of stock';

    this.availabilityEl.hidden = false;
    this.availabilityEl.textContent = available ? availableText : oosText;
    this.availabilityEl.classList.toggle('product-page__availability--available', available);
    this.availabilityEl.classList.toggle('product-page__availability--oos', !available);
  };

  VariantSelector.prototype.refreshSummary = function () {
    var self = this;
    this.cards.forEach(function (card) {
      self.updateCardState(card);
    });

    var ready = this.readyItems();
    var quantity = this.cartQuantity();
    var isProductOos = this.isProductOutOfStock();
    var hasReadyItems = ready.length > 0;
    var hasUnavailableSelection = !hasReadyItems && this.hasUnavailableCompleteSelection();

    if (this.summaryEl) {
      this.summaryEl.textContent = this.formatSelectionSummary();
    }

    if (this.priceEl) {
      if (!this.showSummaryPrice) {
        this.priceEl.hidden = true;
      } else {
        var totalCents = ready.length
          ? ready.reduce(function (sum, item) {
              return sum + item.variant.priceCents * item.quantity;
            }, 0)
          : 0;
        if (this.priceValueEl) {
          this.priceValueEl.textContent = formatMoney(totalCents, this.moneyFormat);
        } else {
          this.priceEl.textContent = formatMoney(totalCents, this.moneyFormat);
        }
        this.priceEl.hidden = !hasReadyItems;
      }
    }

    this.updateProductPagePrice();
    this.updateStockNotice();

    if (!this.addButton) return;

    if (this.footerEl) {
      this.footerEl.classList.toggle('variant-selector__footer--ready', hasReadyItems);
      this.footerEl.classList.toggle('variant-selector__footer--empty', !hasReadyItems);
    } else if (this.gradeSchoolLayout && this.addButton) {
      this.addButton.classList.toggle('variant-selector__atc-button--ready', hasReadyItems);
      this.addButton.classList.toggle('variant-selector__atc-button--empty', !hasReadyItems);
    }

    if (hasReadyItems) {
      this.addButton.disabled = false;
      this.addButton.classList.remove('disabled');
      if (this.addLabel) {
        this.addLabel.hidden = false;
        this.addLabel.textContent = 'Add ' + quantity + ' To Bag';
      }
      if (this.oosLabel) this.oosLabel.classList.add('hidden');
    } else if (isProductOos || hasUnavailableSelection) {
      this.addButton.disabled = true;
      this.addButton.classList.add('disabled');
      if (this.addLabel) this.addLabel.hidden = true;
      if (this.oosLabel) this.oosLabel.classList.remove('hidden');
    } else {
      this.addButton.disabled = true;
      this.addButton.classList.add('disabled');
      if (this.addLabel) {
        this.addLabel.hidden = false;
        var emptyLabel = 'Pick a piece to start';
        if (this.gradeSchoolLayout) {
          var gradeLabels = (this.data.gradeSchoolLabels) || {};
          emptyLabel = gradeLabels.selectOption || 'SELECT OPTION';
        }
        this.addLabel.textContent = emptyLabel;
      }
      if (this.oosLabel) this.oosLabel.classList.add('hidden');
    }
  };

  VariantSelector.prototype.setLoading = function (isLoading) {
    this.isLoading = !!isLoading;
    if (this.root) {
      this.root.classList.toggle('variant-selector--loading', this.isLoading);
    }
    if (this.addButton) {
      if (this.isLoading) {
        this.addButton.disabled = true;
      }
      this.addButton.setAttribute('aria-busy', this.isLoading ? 'true' : 'false');
    }
  };

  VariantSelector.prototype.bindAddToCart = function () {
    var self = this;
    if (!this.addButton) return;
    this.addButton.removeAttribute('onclick');
    this.addButton.addEventListener('click', function (event) {
      event.preventDefault();
      self.addToCart();
    });
  };

  VariantSelector.prototype.collectEmbroidery = function () {
    var box = document.querySelector('#Embroidery .embroidery-text');
    var input = document.querySelector('#Embroidery #embroidery-text');
    var visible = box && box.style.display !== 'none';
    var value = input ? input.value.trim() : '';
    if (!visible || !value) return null;
    return {
      id: EMBROIDERY_VARIANT_ID,
      quantity: 1,
      properties: { For: this.productTitle, Embroidery: value },
    };
  };

  VariantSelector.prototype.showError = function (message) {
    if (!this.errorEl) return;

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.errorEl.focus();

    var self = this;
    this.errorTimeout = setTimeout(function () {
      self.errorEl.hidden = true;
      self.errorEl.textContent = '';
      self.errorTimeout = null;
    }, 3000);
  };

  VariantSelector.prototype.resetAfterAdd = function () {
    var self = this;
    this.cards.forEach(function (card) {
      card.quantityByValue = {};
      card.sharedSelections = {};
      if (!self.isSingleGroup) {
        self.setCardExpanded(card, false);
      }
      self.updateAvailability(card);
    });
    this.refreshSummary();
  };

  VariantSelector.prototype.addToCart = function () {
    var self = this;
    if (this.isLoading) return;

    var ready = this.readyItems();
    if (!ready.length) {
      this.showError('Select a size first!');
      return;
    }

    if (this.totalQuantity() > this.quantityLimit) {
      this.showError(this.quantityLimitMessage());
      return;
    }

    var items = ready.map(function (item) {
      return { id: item.variant.id, quantity: item.quantity };
    });

    var embroidery = this.collectEmbroidery();
    if (embroidery) items.push(embroidery);

    this.setLoading(true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(function () {
        self.resetAfterAdd();
        document.dispatchEvent(
          new CustomEvent('cart:item-added', { detail: { openDrawer: true } })
        );
      })
      .catch(function () {
        self.showError('Something went wrong. Please try again.');
      })
      .finally(function () {
        self.setLoading(false);
        self.refreshSummary();
      });
  };

  VariantSelector.prototype.updateVariantUrl = function (variantId) {
    if (!variantId) return;
    var path = window.location.pathname;
    window.history.replaceState({}, document.title, path + '?variant=' + variantId);
  };

  VariantSelector.prototype.switchGalleryImage = function (imageUrl) {
    if (!imageUrl) return;

    if (window.innerWidth >= 990) {
      var grid = document.getElementById('media-gallery-grid');
      if (!grid) return;
      var items = grid.querySelectorAll('.media-gallery-grid-item');
      items.forEach(function (item) {
        var img = item.querySelector('img');
        if (img && img.src.indexOf(imageUrl) !== -1) {
          grid.insertBefore(item, grid.firstChild);
        }
      });
      return;
    }

    var images = document.querySelectorAll('#media-gallery-root img.media-gallery__main');
    var targetIndex = -1;
    images.forEach(function (img, index) {
      if (img.src.indexOf(imageUrl) !== -1) targetIndex = index;
    });
    if (targetIndex < 0) return;
    var mainSwiper = document.querySelector('.media-gallery-main');
    if (mainSwiper && mainSwiper.swiper) {
      var swiper = mainSwiper.swiper;
      if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(targetIndex, 300);
      } else {
        swiper.slideTo(targetIndex, 300);
      }
    }
  };

  function init() {
    document.querySelectorAll('[data-variant-selector]').forEach(function (root) {
      new VariantSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
